import ModalComponent from '..'
import {
  CustomerAcquisitionChannel,
  CustomerHealthStatus,
  CustomerSort,
  CustomersQuery,
} from '../../../redux-toolkits/customers/customers.type'
import { useGetFulfilmentCentersQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import { Accordion } from '../../accordion'
import { Button } from '../../button'
import { Checkbox } from '../../inputs/Checkbox'

const acquisitionChannels: Array<{
  label: string
  value?: CustomerAcquisitionChannel
}> = [
  { label: 'All acquisition channels' },
  { label: 'Direct', value: 'direct' },
  { label: 'POS', value: 'pos' },
  { label: 'Referral', value: 'referral' },
  { label: 'Ambassador', value: 'ambassador' },
  { label: 'Socials', value: 'socials' },
  { label: 'Other', value: 'other' },
]

export const customerSortOptions: Array<{
  label: string
  value?: CustomerSort
}> = [
  { label: 'Default' },
  { label: 'Recent buyer', value: 'recent_buyers' },
  { label: 'Customer name: A - Z', value: 'customer_name_asc' },
  { label: 'Customer name: Z - A', value: 'customer_name_desc' },
  { label: 'Total order: low to high', value: 'total_orders_asc' },
  { label: 'Total order: high to low', value: 'total_orders_desc' },
  { label: 'Total spend: low to high', value: 'total_spend_asc' },
  { label: 'Total spend: high to low', value: 'total_spend_desc' },
  { label: 'Not onboarded', value: 'not_onboarded' },
  { label: 'Partially onboarded', value: 'partially_onboarded' },
  { label: 'Fully onboarded', value: 'fully_onboarded' },
]

const healthStatuses: Array<{
  label: string
  value?: CustomerHealthStatus
}> = [
  { label: 'All health statuses' },
  { label: 'Active customers', value: 'active' },
  { label: 'Repeat customers', value: 'repeat' },
  { label: 'At-risk customers', value: 'at_risk' },
  { label: 'Churned customers', value: 'churned' },
  { label: 'Not activated customers', value: 'not_activated' },
]

function isRegistrationChannel(name: string): boolean {
  const normalizedName = name.toLowerCase()
  return normalizedName.includes('ikeja') || normalizedName.includes('lekki')
}

export const FilterCustomersModal = ({
  modalOpen,
  handleModalClose,
  setModalOpen,
  query,
  onSuccess,
}: {
  modalOpen: boolean
  handleModalClose: () => void
  setModalOpen: (open: boolean) => void
  query?: CustomersQuery
  onSuccess: (query: CustomersQuery) => void
}) => {
  const { data: fulfilmentCentresResponse, isFetching } =
    useGetFulfilmentCentersQuery(
      { limit: 100 },
      {
        skip: !modalOpen,
        refetchOnMountOrArgChange: true,
      },
    )
  const registrationChannels =
    fulfilmentCentresResponse?.data.docs.filter((centre) =>
      isRegistrationChannel(centre.name),
    ) ?? []

  return (
    <ModalComponent
      open={modalOpen}
      setOpen={setModalOpen}
      onCloseCallback={handleModalClose}
      title={undefined}
      position="right"
    >
      <Button
        label="Reset filters"
        onClick={() => {
          onSuccess({
            ...query,
            skip: 0,
            sort: undefined,
            channel: undefined,
            registrationChannel: undefined,
            healthStatus: undefined,
          })
        }}
        primary={false}
        variant="plain"
        className="!px-0 !py-0 mb-10"
      />

      <Accordion title="Sort by" className="pb-4 border-b" open={true}>
        <div className="flex flex-col gap-2 w-full">
          {customerSortOptions.map(({ label, value }) => (
            <Checkbox
              key={label}
              checkStatus={query?.sort === value}
              handleChange={() => {
                onSuccess({ ...query, skip: 0, sort: value })
              }}
              text={label}
              textPosition="left"
              className="w-full !justify-between !font-normal"
            />
          ))}
        </div>
      </Accordion>

      <Accordion
        title="Acquisition channel"
        className="py-4 border-b"
        open={false}
      >
        <div className="flex flex-col gap-2 w-full">
          {acquisitionChannels.map(({ label, value }) => (
            <Checkbox
              key={label}
              checkStatus={query?.channel === value}
              handleChange={() => {
                onSuccess({ ...query, skip: 0, channel: value })
              }}
              text={label}
              textPosition="left"
              className="w-full !justify-between !font-normal"
            />
          ))}
        </div>
      </Accordion>

      <Accordion
        title="Registration channel"
        className="py-4 border-b"
        open={false}
      >
        <div className="flex flex-col gap-2 w-full">
          <Checkbox
            checkStatus={!query?.registrationChannel}
            handleChange={() => {
              onSuccess({ ...query, skip: 0, registrationChannel: undefined })
            }}
            text="All registration channels"
            textPosition="left"
            className="w-full !justify-between !font-normal"
          />

          {isFetching ? (
            <p className="text-sm text-sec-black">Loading channels...</p>
          ) : (
            registrationChannels.map((centre) => (
              <Checkbox
                key={centre._id}
                checkStatus={query?.registrationChannel === centre._id}
                handleChange={() => {
                  onSuccess({
                    ...query,
                    skip: 0,
                    registrationChannel: centre._id,
                  })
                }}
                text={centre.name}
                textPosition="left"
                className="w-full !justify-between !font-normal"
              />
            ))
          )}
        </div>
      </Accordion>

      <Accordion title="Health status" className="py-4" open={false}>
        <div className="flex flex-col gap-2 w-full">
          {healthStatuses.map(({ label, value }) => (
            <Checkbox
              key={label}
              checkStatus={query?.healthStatus === value}
              handleChange={() => {
                onSuccess({ ...query, skip: 0, healthStatus: value })
              }}
              text={label}
              textPosition="left"
              className="w-full !justify-between !font-normal"
            />
          ))}
        </div>
      </Accordion>
    </ModalComponent>
  )
}
